/**
 * Formula Builder Dialog
 *
 * Dialog for creating/editing calculated columns with formulas.
 * Uses inset-0 + margin:auto centering (instead of translate) to avoid
 * creating a CSS containing block that breaks Monaco's fixedOverflowWidgets.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@loader/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@loader/ui';
import { Input } from '@loader/ui';
import { Label } from '@loader/ui';
import { TooltipProvider } from '@loader/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@loader/ui';
import type { TableFieldApiResponse } from '@loader/shared-types';
import { CALCULATED_COLUMN_DATA_TYPES, FIELD_DATA_TYPE_LABELS } from '@loader/shared-types';

import {
  fetchOperators,
  getAllOperators,
  type SchemaColumn,
} from '@/features/linkedTables/editLinkedTable/formula/operatorConstants';
import { MonacoFormulaEditor, type FormulaEditorHandle } from '@/features/linkedTables/editLinkedTable/formula/MonacoFormulaEditor';
import { FunctionReferencePanel } from '@/features/linkedTables/editLinkedTable/formula/FunctionReferencePanel';
import type { LinkedTableCalculatedColumn, LinkedTableRelationship, LinkedTableVariable } from '@/lib/types';
import { CALCULATED_COLUMNS_TABLE_ALIAS } from '@/lib/constants';
import { logger } from '@/lib/logger';

/**
 * Props for FormulaBuilderDialog component
 */
interface FormulaBuilderDialogProps {
  /** Available fields from the table */
  availableColumns: TableFieldApiResponse[];
  /** Existing calculated columns (can be referenced in new formulas) */
  existingCalculatedColumns: LinkedTableCalculatedColumn[];
  /** Primary table ID */
  table_id: string;
  /** Primary table name/alias (tables[0].alias) */
  primaryTableName: string;
  /** Relationships (for joined tables) */
  relationships: LinkedTableRelationship[];
  /** Available variables (suggested with $ in formulas) */
  variables?: LinkedTableVariable[];
  /** Callback when a calculated column is created */
  onAdd: (calculatedColumn: LinkedTableCalculatedColumn) => void;
  /** Callback when a calculated column is updated */
  onUpdate?: (calculatedColumn: LinkedTableCalculatedColumn) => void;
  /** Optional trigger button text */
  triggerText?: string;
  /** Optional custom trigger renderer */
  renderTrigger?: (onClick: () => void) => React.ReactNode;
  /** Optional calculated column to edit (for edit mode) */
  editColumn?: LinkedTableCalculatedColumn;
  /** Optional controlled open state */
  open?: boolean;
  /** Optional callback when open state changes */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Formula Builder Dialog
 *
 * Allows users to create calculated columns with formulas.
 * Provides helpers for inserting column references and common operations.
 */
export function FormulaBuilderDialog({
  availableColumns,
  existingCalculatedColumns,
  table_id: _primaryTableId,
  primaryTableName,
  relationships,
  variables = [],
  onAdd,
  onUpdate,
  triggerText = 'Add Calculated Column',
  renderTrigger,
  editColumn,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: FormulaBuilderDialogProps) {
  const editorRef = useRef<FormulaEditorHandle>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [formula, setFormula] = useState('');
  const [description, setDescription] = useState('');
  const [dataType, setDataType] = useState<LinkedTableCalculatedColumn['data_type']>('number');
  const [operatorsLoaded, setOperatorsLoaded] = useState(false);

  // Use controlled or internal open state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  // Initialize form with edit data when editColumn changes
  useEffect(() => {
    if (editColumn && open) {
      setName(editColumn.name);
      setFormula(editColumn.formula_value ?? editColumn.formula ?? '');
      setDescription(editColumn.description || '');
      setDataType(editColumn.data_type ?? 'number');
    } else if (!open) {
      // Reset form when dialog closes
      resetForm();
    }
  }, [editColumn, open]);

  // Fetch operators when dialog opens
  useEffect(() => {
    if (open && !operatorsLoaded) {
      fetchOperators()
        .then(() => {
          setOperatorsLoaded(true);
        })
        .catch((error) => {
          logger.debug('Failed to load operators:', error);
        });
    }
  }, [open, operatorsLoaded]);

  // Convert SchemaField[] to SchemaColumn[] for Monaco editor
  const schemaColumns: SchemaColumn[] = useMemo(() => {
    logger.debug('🔍 FormulaBuilder - Rebuilding schema columns');

    const fieldMap = new Map<string, SchemaColumn>();

    availableColumns.forEach((field) => {
      const fieldTableAlias = field.table_alias;
      const isAlreadyPrefixed = field.name.includes('.');

      let key: string;
      let displayId: string;
      let effectiveTableAlias: string | undefined;

      if (isAlreadyPrefixed) {
        key = field.name;
        displayId = field.name;
        effectiveTableAlias = field.name.split('.')[0];
      } else {
        const tableAlias = fieldTableAlias || primaryTableName;
        key = `${tableAlias}.${field.name}`;
        displayId = field.name;
        effectiveTableAlias = tableAlias;
      }

      logger.debug('🔍 FormulaBuilder - Field mapping:', {
        fieldName: field.name,
        originalTableAlias: fieldTableAlias,
        isAlreadyPrefixed,
        effectiveTableAlias,
        key,
      });

      fieldMap.set(key, {
        id: displayId,
        label: field.name,
        description: field.description || undefined,
        data_type: field.data_type as SchemaColumn['data_type'],
        tableAlias: effectiveTableAlias,
      });
    });

    existingCalculatedColumns.forEach((calcCol) => {
      const key = `calculated.${calcCol.name}`;
      fieldMap.set(key, {
        id: calcCol.name,
        label: calcCol.name,
        description: `Calculated: ${calcCol.formula_value}`,
        data_type: (calcCol.data_type as SchemaColumn['data_type']) || 'number',
        tableAlias: CALCULATED_COLUMNS_TABLE_ALIAS,
      });
    });

    const result = Array.from(fieldMap.values());
    logger.debug('FormulaBuilder - Final schema columns (deduplicated)', { count: result.length });
    return result;
  }, [availableColumns, existingCalculatedColumns, primaryTableName]);

  const validateColumnName = (columnName: string): string | null => {
    const trimmedName = columnName.trim();

    if (!trimmedName) {
      return null;
    }

    const isDuplicate = existingCalculatedColumns.some(col =>
      col.name.toLowerCase() === trimmedName.toLowerCase() &&
      col.id !== editColumn?.id
    );

    if (isDuplicate) {
      return `A calculated column named "${trimmedName}" already exists`;
    }

    return null;
  };

  const resetForm = () => {
    setName('');
    setNameError(null);
    setFormula('');
    setDescription('');
    setDataType('number');
  };

  const convertFormula = (
    editorFormula: string
  ): { formula_id: string; formula_value: string } => {
    let formulaId = editorFormula;
    let formulaValue = editorFormula;

    const columnReferences = editorFormula.match(/\[([^\]]+)\]/g) || [];

    for (const ref of columnReferences) {
      const content = ref.slice(1, -1);

      let field = availableColumns.find((f) => {
        const isAlreadyPrefixed = f.name.includes('.');
        if (isAlreadyPrefixed) {
          return f.name === content;
        }
        return false;
      });

      let tableAlias: string | undefined;
      let fieldName: string | undefined;
      let tableId = _primaryTableId;
      let tableName = primaryTableName;

      if (field) {
        const prefixParts = field.name.split('.');
        tableAlias = prefixParts[0];
        fieldName = field.name;

        const rel = relationships.find((r) => r.alias === tableAlias);
        if (rel) {
          tableId = rel.target_id;
          tableName = tableAlias;
        }
      } else {
        const parts = content.split('.');
        if (parts.length === 2) {
          [tableAlias, fieldName] = parts;

          field = availableColumns.find((f) => {
            const fieldTableAlias = f.table_alias;
            const effectiveTableAlias = fieldTableAlias || primaryTableName;
            return effectiveTableAlias === tableAlias && f.name === fieldName;
          });

          if (field) {
            if (tableAlias !== primaryTableName) {
              const rel = relationships.find((r) => r.alias === tableAlias);
              if (rel) {
                tableId = rel.target_id;
                tableName = tableAlias;
              }
            }
          }
        }
      }

      if (field && tableAlias && fieldName) {
        const uuidRef = `[${tableId}.${field.id}]`;
        formulaId = formulaId.replace(ref, uuidRef);

        const isPrePrefixed = field.name.includes('.');
        const displayFieldName = isPrePrefixed ? field.name : `${tableName}.${fieldName}`;
        const nameRef = `[${displayFieldName}]`;
        formulaValue = formulaValue.replace(ref, nameRef);
      }
    }

    return { formula_id: formulaId, formula_value: formulaValue };
  };

  const handleSubmit = () => {
    const nameValidationError = validateColumnName(name);
    if (nameValidationError) {
      setNameError(nameValidationError);
      return;
    }

    if (!name || !formula) {
      return;
    }

    const { formula_id, formula_value } = convertFormula(formula.trim());

    const calculatedColumn: LinkedTableCalculatedColumn = {
      id: editColumn?.id || crypto.randomUUID(),
      name: name.trim(),
      formula: formula_value,
      formula_id,
      formula_value,
      description: description.trim() || undefined,
      data_type: dataType,
      dependencies: editColumn?.dependencies || [],
    };

    if (editColumn && onUpdate) {
      logger.debug('FormulaBuilderDialog - Updating calculated column', { name: calculatedColumn.name });
      onUpdate(calculatedColumn);
    } else {
      logger.debug('FormulaBuilderDialog - Creating calculated column', { name: calculatedColumn.name });
      onAdd(calculatedColumn);
    }

    resetForm();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {renderTrigger ? (
        renderTrigger(() => setOpen(true))
      ) : (
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm" className="w-full justify-start">
            <Plus className="mr-2 h-4 w-4" />
            {triggerText}
          </Button>
        </DialogTrigger>
      )}
      {/* Use inset-0 + margin:auto centering instead of translate(-50%,-50%) to avoid
          creating a CSS containing block that breaks Monaco's fixedOverflowWidgets */}
      <DialogContent
        className="max-h-[90vh] sm:max-w-[700px] !top-0 !left-0 !right-0 !bottom-0 !m-auto !h-fit"
        style={{ translate: 'none' }}
      >
        <DialogHeader>
          <DialogTitle>
            {editColumn ? 'Edit Calculated Column' : 'Create Calculated Column'}
          </DialogTitle>
          <DialogDescription>
            {editColumn ? 'Update' : 'Create'} a virtual column using a formula. Type <code>[</code> to
            reference columns{variables.length > 0 ? <>, <code>$</code> to insert variables</> : ''}, and the system will suggest available operators
            and functions.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4 max-h-[70vh] overflow-y-auto overflow-x-hidden">
          {/* Column Name Input */}
          <div className="grid gap-2">
            <Label htmlFor="name">Column Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Total Price"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError(validateColumnName(e.target.value));
              }}
              className={`bg-background ${nameError ? 'border-destructive' : ''}`}
            />
            {nameError ? (
              <p className="text-destructive text-xs">{nameError}</p>
            ) : (
              <p className="text-muted-foreground text-xs">
                Name for the calculated column (shown in table header)
              </p>
            )}
          </div>

          {/* Data Type Selector */}
          <div className="grid gap-2">
            <Label htmlFor="dataType">Data Type *</Label>
            <Select value={dataType} onValueChange={(value) => setDataType(value as LinkedTableCalculatedColumn['data_type'])}>
              <SelectTrigger id="data_type" className="bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CALCULATED_COLUMN_DATA_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {FIELD_DATA_TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              Type of data this calculated column will produce
            </p>
          </div>

          {/* Formula Input with Monaco Editor */}
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="formula">Formula *</Label>
              {operatorsLoaded && (
                <TooltipProvider>
                  <FunctionReferencePanel
                    operators={getAllOperators()}
                    onInsert={(text) => editorRef.current?.insertText(text)}
                  />
                </TooltipProvider>
              )}
            </div>
            <div className="h-[4.5rem]">
              <MonacoFormulaEditor
                ref={editorRef}
                value={formula}
                onChange={setFormula}
                schema={schemaColumns}
                variables={variables}
                height="100%"
              />
            </div>
            <p className="text-muted-foreground text-xs">
              Type <code className="bg-muted rounded px-1 py-0.5">[</code> to
              reference columns. Errors will be highlighted in real-time.
            </p>
          </div>

          {/* Description Input (Optional) */}
          <div className="grid gap-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Input
              id="description"
              placeholder="e.g., Calculates the total price including tax"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-background"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              resetForm();
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!name || !formula || !!nameError}
          >
            {editColumn ? 'Update Column' : 'Create Column'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
